import { messageErreur } from '@imp/shared/message-erreur';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ClipboardList, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/modules/auth/AuthContext';
import { listQuestionnaires } from '@/modules/qcm/api';
import { desactiverQuestionnaire } from '@/modules/qcm/builder';

export function QcmPickPage() {
  const queryClient = useQueryClient();
  const { profil } = useAuth();
  const isAdmin = profil?.isAdmin ?? false;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['questionnaires'],
    queryFn: listQuestionnaires,
  });
  const modeles = data ?? [];

  const desactiverMut = useMutation({
    mutationFn: (id: string) => desactiverQuestionnaire(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon">
            <Link to="/devis" aria-label="Retour">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Devis guidé</h1>
            <p className="text-muted-foreground">
              Répondez à un questionnaire : le devis est généré automatiquement.
            </p>
          </div>
        </div>
        {isAdmin ? (
          <Button asChild variant="outline">
            <Link to="/devis/qcm/builder">
              <Plus className="h-4 w-4" /> Créer un devis guidé
            </Link>
          </Button>
        ) : null}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Chargement…</p>
      ) : isError ? (
        <p className="text-destructive">Erreur : {messageErreur(error)}</p>
      ) : modeles.length === 0 ? (
        <Card className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <p className="text-muted-foreground">Aucun devis guidé pour le moment.</p>
          {isAdmin ? (
            <Button asChild variant="outline">
              <Link to="/devis/qcm/builder">
                <Plus className="h-4 w-4" /> Créer le premier
              </Link>
            </Button>
          ) : null}
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {modeles.map((m) => (
            <Card key={m.id} className="flex h-full flex-col">
              <CardHeader>
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-lg text-white"
                  style={{ backgroundColor: m.couleur ?? '#dc2626' }}
                >
                  <ClipboardList className="h-5 w-5" />
                </div>
                <CardTitle className="pt-2">{m.nom}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col">
                <p className="flex-1 text-sm text-muted-foreground">{m.description ?? ''}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button asChild variant="secondary">
                    <Link to={`/devis/qcm/${m.id}`}>Remplir</Link>
                  </Button>
                  {isAdmin ? (
                    <>
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/devis/qcm/builder/${m.id}`}>Modifier</Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={desactiverMut.isPending}
                        onClick={() => {
                          desactiverMut.mutate(m.id);
                        }}
                      >
                        Désactiver
                      </Button>
                    </>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
